/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.application

import uk.co.nickthecoder.glok.property.boilerplate.*
import uk.co.nickthecoder.glok.util.currentTimeMillis

/*
* Implementation Notes :
*
* Timers may start or stop other timers, so we must be careful not to cause concurrent modification exceptions
* when iterating through the timers.
*
* This is achieved by [start] and [stop] NEVER adding or removing from the `running` list.
* The `pending` list is changed by [start], but when we iterate over this list, only OUR code runs,
* so 3rd party code cannot cause concurrent modifications.
*
* The extra `pending` list and the states `PENDING` and `STOPPING` exist solely to avoid concurrent modification
* exceptions, and without having to clone the `running` list every time round glok's event loop.
*
* NOTE. An earlier implementation used an OrderedSet for the `running` collection. But Javascript doesn't
* have this class. Using OrderedSet was more efficient for large number of Timers.
*/

/**
 * Runs code after a given time interval.
 * This is not a high precision timer, there is no guarantee about how soon after [delay] milliseconds,
 * the [action] will be performed.
 *
 * The timer is _not_ started on creation. You must call [start].
 *
 * [delay] is measured in milliseconds, and stored as a Double for compatibility with javascript,
 * which doesn't support the `Long` type.
 *
 * If [repeating] == `false`, [action] is performed only once, and then the timer stops.
 * Otherwise, [action] will be repeated every [delay] milliseconds until the timer is [stopped][stop].
 */
class GlokTimer(

    delay: Double,
    repeating: Boolean = false,
    val action: () -> Unit

) : Comparable<GlokTimer> {

    /**
     * Changing a timer to repeating after it has already fired does _NOT_ cause it to fire again;
     * Call [start] again if required.
     *
     * Setting repeating to `false` for an active repeating timer will cause it to stop after it next fires.
     */
    val repeatingProperty by booleanProperty(repeating)
    var repeating by repeatingProperty

    /**
     * NOTE. Changing the delay of a started timer does NOT affect the time that it will next be fired.
     * However, it _will_ affect the delay for subsequent firings of [repeating] timers.
     */
    val delayProperty by doubleProperty(delay)
    var delay by delayProperty

    private val statusProperty by intProperty(STOPPED)
    private var status: Int = STOPPED

    /**
     * * Initially `false`.
     * * `true` after [start] is called.
     * * `false` after [stop] is called.
     * * `false` after the timer fires and [repeating] == `false`.
     */
    val isActiveProperty: ObservableBoolean = BooleanUnaryFunction(statusProperty) { status ->
        status == PENDING || status == ACTIVE
    }
    val isActive by isActiveProperty

    private var dueTimestamp: Double = 0.0

    /**
     * Allows timers to be ordered by the time they are next due to fire.
     */
    override fun compareTo(other: GlokTimer): Int {
        return dueTimestamp.compareTo(other.dueTimestamp)
    }

    /**
     * A timer is not started on creation, so this must be called at least once for the timer to have any effect.
     *
     * You may call start _again_, before [action] had been performed, in which case, the timer resets,
     * and it will be at least [delay] milliseconds before [action] is performed.
     *
     * @return `this` - a convenience which allow code such as this :
     *
     *     val myTimer = Timer( 100 ) {...}.start()
     */
    fun start(): GlokTimer {

        dueTimestamp = currentTimeMillis() + delay

        if (status == ACTIVE) return this

        status = PENDING

        if (! pending.contains(this)) {
            pending.add(this)
        }

        return this
    }

    fun stop() {
        status = STOPPING
    }


    companion object {
        /**
         * Timers are [STOPPED] when first created. Non-repeating timers are also [STOPPED] after they have completed.
         * Any timer (repeating or one-off) are [STOPPED] shortly after [stop] is called (but not immediately)
         */
        private const val STOPPED = 0

        /**
         * Pending timers are in the [pending] list.
         * When [processAll] is next called, they will transition to [ACTIVE] and moved to [active] list.
         */
        private const val PENDING = 1

        /**
         * Active timers are in the [active] list.
         * To become active, they were first started (using [start]), and then when [processAll] was next called,
         * they transitioned from [PENDING] to [ACTIVE].
         * If they are not [repeating], they will then transition to [STOPPED].
         */
        private const val ACTIVE = 2

        /**
         * The state after [stop] is called, but before [processAll] has been called.
         * When [processAll] find a [STOPPING] timer in the [active] list, it is removed from the list
         * without calling [action]. Its [status] is then [STOPPED].
         */
        private const val STOPPING = 3

        /**
         * A list of [GlokTimer]s which have been started using [start] and therefore their [status] is [PENDING].
         * The next time [processAll] is called, these timers will be moved into [active],
         * or just removed if [stop] was called between [start] and [processAll].
         */
        private val pending = mutableListOf<GlokTimer>()

        /**
         * Timers enter this list when [processAll] find a timer in [pending] which is [active].
         * Non-repeating timers only stay in this list momentarily, because they will be removed before
         * [processAll] completes.
         * Repeating timers stay in this until both [stop] and [processAll] are called.
         *
         * NOTE. This list contains timers whose state is [STOPPING] as well as [ACTIVE].
         */
        private val active = mutableListOf<GlokTimer>()

        internal fun clearTimers() {
            pending.clear()
            active.clear()
        }

        internal fun processAll() {
            val now = currentTimeMillis()

            if (pending.isNotEmpty()) {
                val iterator = pending.iterator()
                while (iterator.hasNext()) {
                    val timer = iterator.next()

                    when (timer.status) {
                        PENDING -> {
                            active.add(timer)
                            timer.status = ACTIVE
                        }

                        STOPPING -> {
                            timer.status = STOPPED
                        }
                    }

                    // Regardless of its previous state, it is no longer pending, so remove from the `pending` list.
                    iterator.remove()
                }
            }

            if (active.isNotEmpty()) {
                val iterator = active.iterator()
                while (iterator.hasNext()) {
                    val timer = iterator.next()

                    if (timer.status == STOPPING) {
                        timer.status = STOPPED
                        iterator.remove()

                    } else {

                        if (timer.dueTimestamp > now) {
                            // Not due yet...
                            continue
                        }

                        // The timer is due.
                        try {
                            timer.action()
                        } finally {
                            if (timer.repeating) {
                                timer.dueTimestamp = now + timer.delay
                            } else {
                                timer.status = STOPPED
                                iterator.remove()
                            }
                        }
                    }
                }
            }

        }
    }

}
